import { ComboV3, TenantLandingResponse } from "@/src/types/tenant";

const TENANT_LANDING_URL = `${process.env.NEXT_PUBLIC_API_URL}/tenant/landing`;
const TENANT_LANDING_LIST_URL = `${process.env.NEXT_PUBLIC_API_URL}/tenant/landing-list`;
const PRODUCT_SERVICE_URL = process.env.NEXT_PUBLIC_PRODUCT_SERVICE_URL;

/**
 * How long a tenant's landing payload may be reused from Next's Data Cache.
 *
 * This response is the storefront's hot path: six routes await it on EVERY
 * navigation, and it is large — ~988 KB / 394 products for a mid-size catalogue,
 * measured at 0.44–0.79 s against a LOCAL api. Re-fetching it per navigation was
 * the single biggest source of click latency.
 *
 * The payload is keyed only by `prefix` (no auth, no per-buyer data), so it is
 * safe to share across visitors. The tradeoff is staleness: a seller editing a
 * product may wait up to this long to see it on the live storefront. Stock and
 * price shown from cache are advisory — checkout re-validates server-side.
 *
 * Override per environment with SELLER_WEB_LANDING_TTL (seconds); 0 disables
 * caching entirely and restores the old fetch-every-time behaviour.
 */
const LANDING_TTL = Number(process.env.SELLER_WEB_LANDING_TTL ?? 60);

/** Cache tag for one tenant's landing data, so a publish can purge just that store. */
export const landingCacheTag = (prefix: string) => `tenant-landing:${prefix}`;

/**
 * Fetch a single combo by Mongo `_id` from the v3 buyer endpoint.
 * Returns `null` when the id isn't a Mongo ObjectId (legacy int id) or the
 * server returns non-200 — callers fall back to landing-page combos.
 */
export async function getComboV3ById(id: string): Promise<ComboV3 | null> {
  if (!PRODUCT_SERVICE_URL) return null;
  if (!/^[a-f0-9]{24}$/i.test(id)) return null;
  try {
    const res = await fetch(`${PRODUCT_SERVICE_URL.replace(/\/+$/, '')}/v3/buyer/combo/${id}`, {
      cache: 'no-store',
      headers: { accept: 'application/json' },
    });
    if (!res.ok) return null;
    const body = await res.json();
    // v3 returns `{ combo, relevantProducts, outletRating, outletReviewCount }`;
    // tolerate the legacy flat-combo shape during rollout.
    const data = body?.data;
    if (!data) return null;
    if (data.combo) return data.combo as ComboV3;
    // Legacy: data itself was the combo object.
    return data as ComboV3;
  } catch (err) {
    console.error('getComboV3ById error:', err);
    return null;
  }
}

export interface OutletCouponOption {
  id: number;
  code: string;
  name?: string;
  description?: string;
  /** portal-api returns these on notApplicable entries so we can show why. */
  reason?: string;
  mrp?: string | number;
  amount?: string | number;
  type?: string;
  startDate?: string;
  endDate?: string;
  /** Cap on a percentage coupon. */
  maxDiscount?: string | number;
  /** What this coupon is worth on the cart value we asked about. Was always 0
   *  before portal-api's applicable-list fix; now a real number. */
  discountAmount?: number;
  /** 'global' = GrabLocal-wide, valid at every store. 'outlet' = one seller's. */
  scope?: 'outlet' | 'global';
}

/**
 * Fetch coupons for an outlet (portal-api). Returns both buckets:
 *   - applicable: usable right now at this outlet + cartValue
 *   - notApplicable: exist for the outlet but the buyer doesn't qualify yet
 *     (threshold not met, time window inactive, etc.) — surfaced greyed out
 *     in the picker so the buyer knows the path forward.
 *
 * cartValue is the outlet's current pre-discount subtotal; portal-api uses it
 * to split the two buckets.
 */
export async function getApplicableCouponsForOutlet(
  outletId: string,
  cartValue: number
): Promise<{ applicable: OutletCouponOption[]; notApplicable: OutletCouponOption[] }> {
  const empty = { applicable: [] as OutletCouponOption[], notApplicable: [] as OutletCouponOption[] };
  const base = process.env.NEXT_PUBLIC_API_URL;
  if (!base || !outletId) return empty;
  try {
    // NEXT_PUBLIC_API_URL already includes the /api/v1 prefix — don't double it.
    const url = new URL(`${base.replace(/\/+$/, '')}/coupons/applicable`);
    url.searchParams.set('outletId', outletId);
    url.searchParams.set('cartValue', String(cartValue || 0));
    const res = await fetch(url.toString(), {
      cache: 'no-store',
      headers: { accept: 'application/json' },
    });
    if (!res.ok) return empty;
    const body = await res.json();
    const data = body?.response?.data ?? body?.data ?? {};
    const applicable = Array.isArray(data.applicable) ? data.applicable : [];
    const notApplicable = Array.isArray(data.notApplicable) ? data.notApplicable : [];
    return { applicable, notApplicable };
  } catch (err) {
    console.error('getApplicableCouponsForOutlet error:', err);
    return empty;
  }
}

/**
 * Find a v3 combo by its portal-api legacy integer id. Used by the combo
 * detail page so an old URL like /shop/combo/8 (saved before the v3 cutover)
 * still surfaces the latest migrated data instead of falling back to stale
 * landing-page snapshots.
 */
export async function getComboV3ByLegacyId(
  legacyComboId: number,
  outletId?: string
): Promise<ComboV3 | null> {
  if (!PRODUCT_SERVICE_URL || !outletId) return null;
  try {
    const res = await fetch(
      `${PRODUCT_SERVICE_URL.replace(/\/+$/, '')}/v3/buyer/combo/outlet/${encodeURIComponent(outletId)}`,
      { cache: 'no-store', headers: { accept: 'application/json' } }
    );
    if (!res.ok) return null;
    const body = await res.json();
    // v3 returns `{ combos, outletRating, outletReviewCount }`; tolerate the
    // legacy array shape during rollout.
    const list: ComboV3[] = Array.isArray(body?.data)
      ? body.data
      : Array.isArray(body?.data?.combos)
      ? body.data.combos
      : [];
    return list.find((c) => c.legacyComboId === legacyComboId) ?? null;
  } catch (err) {
    console.error('getComboV3ByLegacyId error:', err);
    return null;
  }
}

/**
 * Fetch every active combo for the outlet from v3 — used by the shop landing
 * "Bundle & save" rail so it links to and adds against the v3 combo ids.
 * Returns `[]` on any failure; callers can fall back to landing-page combos.
 */
export async function getOutletCombosV3(outletId: string): Promise<ComboV3[]> {
  if (!PRODUCT_SERVICE_URL || !outletId) return [];
  try {
    const res = await fetch(
      `${PRODUCT_SERVICE_URL.replace(/\/+$/, '')}/v3/buyer/combo/outlet/${encodeURIComponent(outletId)}`,
      // The second serial hop on the /shop render (it needs outletId from the
      // landing call above), so it lands directly on the critical path. Same TTL
      // and same reasoning as the landing payload — outlet-scoped, not buyer-scoped.
      { next: { revalidate: LANDING_TTL, tags: [`outlet-combos:${outletId}`] }, headers: { accept: 'application/json' } }
    );
    if (!res.ok) return [];
    const body = await res.json();
    // v3 returns `{ combos, outletRating, outletReviewCount }`; tolerate the
    // legacy array shape during rollout.
    return Array.isArray(body?.data)
      ? body.data
      : Array.isArray(body?.data?.combos)
      ? body.data.combos
      : [];
  } catch (err) {
    console.error('getOutletCombosV3 error:', err);
    return [];
  }
}


export async function getTenantLandingService(
  prefix: string
): Promise<TenantLandingResponse> {
  try {
    const url = new URL(TENANT_LANDING_URL);

    url.searchParams.set("prefix", prefix);

    const response = await fetch(url.toString(), {
      // Reuse across navigations — see LANDING_TTL. Tagged per tenant so the CRM
      // can call revalidateTag(landingCacheTag(prefix)) on publish for an
      // instant purge instead of waiting out the window.
      next: { revalidate: LANDING_TTL, tags: [landingCacheTag(prefix)] },
      headers: {
        accept: "application/json",
      },
    });

    if (!response.ok) {
      return {
        success: false,
        message: `Tenant lookup failed with ${response.status}`,
      };
    }
    return response.json();
  } catch (error) {
    console.error("Tenant Service Error:", error);

    return {
      success: false,
      message: "Something went wrong",
    };
  }
}

export interface LandingTenant {
  tenantId: number;
  tenantName: string;
  legalEntityName?: string | null;
  prefix: string;
  /** portal-api approval state — 'active' / 'verified' are the publishable ones. */
  approvalStatus?: string | null;
  isActive?: boolean | null;
  isApproved?: boolean | null;
  additionalInfo?: { logoUrl?: string | null; businessBrief?: string | null } | null;
  businessType?: { businessTypeName?: string | null } | null;
  outlets?: { outletName?: string | null }[];
}

/**
 * Every tenant that is a real, reachable storefront.
 *
 * Applies the SAME two gates as isKnownTenant() in src/lib/tenant-host.ts, and
 * it must keep doing so. Both consumers of this list turn a tenant into a link —
 * MoreNeighbors renders a card that navigates to <prefix>.grablocal.in, and the
 * dev-apex sitemap branch emits <prefix>.../sitemap.xml — so anything this
 * returns that tenant-host would reject becomes a link straight into a 404.
 *
 * `approvalStatus` / `isActive` / `isApproved` are each checked only when
 * present, so this app can deploy ahead of the portal-api change that adds those
 * fields without blanking the section.
 */
export async function getLandingTenantsService(): Promise<LandingTenant[]> {
  try {
    const response = await fetch(TENANT_LANDING_LIST_URL, {
      cache: "no-store",
      headers: { accept: "application/json" },
    });
    if (!response.ok) return [];
    const data = await response.json();
    const tenants: LandingTenant[] = data.tenants ?? [];
    if (!Array.isArray(tenants)) return [];

    return tenants.filter((t) => {
      if (!t?.outlets?.length) return false;
      if (t.isActive === false || t.isApproved === false) return false;
      const status = t.approvalStatus?.trim().toLowerCase();
      return !status || status === "active" || status === "verified";
    });
  } catch (error) {
    console.error("getLandingTenantsService error:", error);
    return [];
  }
}

/**
 * Fetch GrabLocal-wide coupons (portal-api). Same two buckets as
 * getApplicableCouponsForOutlet, and deliberately the same shape so one
 * renderer serves both.
 *
 * No outletId: a GrabLocal coupon belongs to the cart, not a store, and the
 * buyer may be browsing offers with no cart at all. `cartValue` of 0 is a
 * legitimate call — every coupon with a minimum comes back under notApplicable
 * carrying its reason, which is what drives the "Add ₹X more to unlock" line.
 *
 * `buyerUserId` omits coupons this buyer has already used up, so the list never
 * offers something checkout will refuse.
 */
export async function getGlobalCoupons(
  cartValue: number,
  buyerUserId?: string | number
): Promise<{ applicable: OutletCouponOption[]; notApplicable: OutletCouponOption[] }> {
  const empty = { applicable: [] as OutletCouponOption[], notApplicable: [] as OutletCouponOption[] };
  const base = process.env.NEXT_PUBLIC_API_URL;
  if (!base) return empty;
  try {
    // NEXT_PUBLIC_API_URL already includes the /api/v1 prefix — don't double it.
    const url = new URL(`${base.replace(/\/+$/, '')}/coupons/global`);
    url.searchParams.set('cartValue', String(cartValue || 0));
    if (buyerUserId) url.searchParams.set('buyerUserId', String(buyerUserId));
    const res = await fetch(url.toString(), {
      cache: 'no-store',
      headers: { accept: 'application/json' },
    });
    if (!res.ok) return empty;
    const body = await res.json();
    const data = body?.response?.data ?? body?.data ?? {};
    const applicable = Array.isArray(data.applicable) ? data.applicable : [];
    const notApplicable = Array.isArray(data.notApplicable) ? data.notApplicable : [];
    return { applicable, notApplicable };
  } catch (err) {
    console.error('getGlobalCoupons error:', err);
    return empty;
  }
}
